import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch, apiDownload } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import ApiInspector from '../../components/ApiInspector';
import GuidaValoriAmmessi from '../../components/GuidaValoriAmmessi';
import { API_DOCS } from './ArticoliIntercambiabiliPage.api';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface GruppoIntercambiabile {
  articoloPadreId: number;
  codicePadre: string;
  descrizionePadre: string;
  categoriaMerceologicaId: number | null;
  nomeCategoria: string | null;
  societaId: number;
  nomeSocieta: string | null;
  nFigli: number;
}

interface ArticoloFiglio {
  id: number;
  articoloFiglioId: number;
  codice: string;
  descrizione: string;
  um: string | null;
  umVenditaId: number | null;
  fornitore: string | null;
  prezzo: number | null;
}

interface GruppoDettaglio {
  articoloPadre: {
    id: number;
    codice: string;
    descrizione: string;
    categoriaMerceologicaId: number | null;
    nomeCategoria: string | null;
    umVenditaId: number | null;
    umVenditaSimbolo: string | null;
  };
  figli: ArticoloFiglio[];
}

interface ArticoloOpzione {
  id: number;
  codice: string;
  descrizioneBase: string;
  categoriaMerceologicaId?: number | null;
  categoriaMerceologica?: { id: number; nome: string } | null;
  umVenditaId?: number | null;
  umVendita?: { id: number; simbolo: string } | null;
}

interface CategoriaMerceologica {
  id: number;
  nome: string;
}

// ── Componente Autocomplete Articolo ─────────────────────────────────────────

interface AutocompleteArticoloProps {
  placeholder: string;
  societaId: string;
  tipologia?: string;
  escludiIds?: number[];
  onSelect: (a: ArticoloOpzione) => void;
  disabled?: boolean;
  value?: string;
  padreCategoriaMerceologicaId?: number | null;
  padreUmVenditaId?: number | null;
}

function AutocompleteArticolo({
  placeholder,
  societaId,
  tipologia,
  escludiIds = [],
  onSelect,
  disabled = false,
  value = '',
  padreCategoriaMerceologicaId,
  padreUmVenditaId,
}: AutocompleteArticoloProps) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<ArticoloOpzione[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim() || query.length < 2) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: '20' });
        if (societaId) params.set('societaId', societaId);
        if (tipologia) params.set('tipologia', tipologia);
        // @api GET /api/anagrafiche/articoli?q=&limit=20&societaId=&tipologia= -> {"data":[{"id":"number","codice":"string","descrizioneBase":"string"}]}
        const res = await apiFetch<{ data: ArticoloOpzione[] }>(`/api/anagrafiche/articoli?${params}`);
        const filtered = (res.data ?? []).filter(a => !escludiIds.includes(a.id));
        setResults(filtered);
        setOpen(filtered.length > 0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, societaId, tipologia]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(a: ArticoloOpzione) {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(a);
  }

  return (
    <div className="intc-autocomplete-wrapper" ref={wrapperRef}>
      <input
        type="text"
        className="bop-input intc-autocomplete-input"
        placeholder={placeholder}
        value={query}
        onChange={e => setQuery(e.target.value)}
        disabled={disabled}
        autoComplete="off"
      />
      {loading && <span className="intc-autocomplete-spinner">⟳</span>}
      {open && results.length > 0 && (
        <ul className="intc-autocomplete-dropdown">
          {results.map(a => {
            const catId = a.categoriaMerceologica?.id ?? null;
            const umId  = a.umVenditaId ?? a.umVendita?.id ?? null;
            const catMatch = padreCategoriaMerceologicaId != null
              ? catId === padreCategoriaMerceologicaId
              : null;
            const umMatch  = padreUmVenditaId != null
              ? umId === padreUmVenditaId
              : null;
            let colorClass = '';
            if (catMatch !== null || umMatch !== null) {
              const allMatch = (catMatch !== false) && (umMatch !== false);
              colorClass = allMatch
                ? 'intc-autocomplete-item--match'
                : 'intc-autocomplete-item--mismatch';
            }
            return (
              <li
                key={a.id}
                className={`intc-autocomplete-item ${colorClass}`}
                onMouseDown={() => handleSelect(a)}
              >
                <span className="intc-autocomplete-item-codice">{a.codice}</span>
                <span className="intc-autocomplete-item-desc">{a.descrizioneBase}</span>
                {a.umVendita && (
                  <span className={`intc-autocomplete-item-um ${umMatch === false ? 'intc-autocomplete-item-um--mismatch' : ''}`}>
                    {a.umVendita.simbolo}
                  </span>
                )}
                {a.categoriaMerceologica && (
                  <span className="intc-autocomplete-item-categoria">{a.categoriaMerceologica.nome}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────

export default function ArticoliIntercambiabiliPage() {
  const { can } = usePermesso();
  const canEdit = can('archivi.articoli_intercambiabili', 'edit');
  const canDelete = can('archivi.articoli_intercambiabili', 'delete');

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // ── Stato lista ────────────────────────────────────────────────────────────
  const [gruppi, setGruppi] = useState<GruppoIntercambiabile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // ── Filtri ─────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterCategoriaId, setFilterCategoriaId] = useState('');
  const [categorie, setCategorie] = useState<CategoriaMerceologica[]>([]);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Drawer dettaglio ───────────────────────────────────────────────────────
  const [drawerGruppo, setDrawerGruppo] = useState<GruppoDettaglio | null>(null);
  const [drawerPadreId, setDrawerPadreId] = useState<number | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // ── Modal crea/modifica ────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editPadreId, setEditPadreId] = useState<number | null>(null);
  const [editPadreLabel, setEditPadreLabel] = useState('');
  const [modalPadre, setModalPadre] = useState<ArticoloOpzione | null>(null);
  const [modalFigli, setModalFigli] = useState<ArticoloOpzione[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Import Excel ───────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    stato: string;
    totaleRighe: number;
    padreCreati: number;
    padreRiusati: number;
    gruppiAggiornati: number;
    righeErrore: number;
    logTesto: string;
    fatalError: string | null;
  } | null>(null);

  async function handleScaricaTemplate() {
    try {
      // @api GET /api/articoli-intercambiabili/template -> file xlsx
      await apiDownload(
        `/api/articoli-intercambiabili/template?_t=${Date.now()}`,
        'template_articoli_intercambiabili.xlsx'
      );
    } catch (err: any) {
      showToast('Errore download template: ' + err.message, 'error');
    }
  }

  function openFilePicker() {
    if (!filterSocietaId) {
      showToast('Seleziona prima una società', 'error');
      return;
    }
    fileInputRef.current?.click();
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permette di re-importare lo stesso file
    if (!file) return;
    if (!filterSocietaId) {
      showToast('Seleziona prima una società', 'error');
      return;
    }
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('societaId', filterSocietaId);
      // @api POST /api/articoli-intercambiabili/import (multipart: file, societaId) -> {"success":"true","data":{"stato":"string","totaleRighe":"number","padreCreati":"number","padreRiusati":"number","gruppiAggiornati":"number","righeErrore":"number","logTesto":"string","fatalError":"string|null"}}
      const res = await apiFetch<{
        success: boolean;
        data: {
          stato: string;
          totaleRighe: number;
          padreCreati: number;
          padreRiusati: number;
          gruppiAggiornati: number;
          righeErrore: number;
          logTesto: string;
          fatalError: string | null;
        };
      }>('/api/articoli-intercambiabili/import', { method: 'POST', body: formData });
      setImportResult(res.data);
      await fetchGruppi(1);
    } catch (err: any) {
      showToast('Errore import: ' + err.message, 'error');
    } finally {
      setIsImporting(false);
    }
  }

  // ── Carica categorie merceologiche ─────────────────────────────────────────
  useEffect(() => {
    if (!pronta) return;
    const params = new URLSearchParams({ pageSize: '200' });
    if (filterSocietaId) params.set('societaId', filterSocietaId);
    // @api GET /api/categorie-merceologiche?pageSize=200&societaId= -> {"data":[{"id":"number","nome":"string"}]}
    apiFetch<{ data: CategoriaMerceologica[] }>(`/api/categorie-merceologiche?${params}`)
      .then(res => setCategorie(res.data ?? []))
      .catch(() => setCategorie([]));
  }, [pronta, filterSocietaId]);

  // Chiamata effettuata dal componente GuidaValoriAmmessi (sezione guida import)
  // @api GET /api/anagrafiche/articoli/valori-ammessi?societaId=N -> {"success":true,"data":"ValoriAmmessi"}

  // ── Fetch gruppi ───────────────────────────────────────────────────────────
  const fetchGruppi = useCallback(async (pg = 1) => {
    if (!pronta) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pg), pageSize: String(PAGE_SIZE) });
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      if (search.trim()) params.set('search', search.trim());
      if (filterCategoriaId) params.set('categoriaMerceologicaId', filterCategoriaId);
      // @api GET /api/articoli-intercambiabili?page=1&pageSize=20&societaId=&search=&categoriaMerceologicaId= -> {"success":"true","data":[{"articoloPadreId":"number","codicePadre":"string","descrizionePadre":"string","nFigli":"number"}],"total":"number"}
      const res = await apiFetch<{ success: boolean; data: GruppoIntercambiabile[]; total: number }>(
        `/api/articoli-intercambiabili?${params}`
      );
      setGruppi(res.data ?? []);
      setTotal(res.total ?? 0);
      setPage(pg);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento gruppi', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [pronta, filterSocietaId, search, filterCategoriaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchGruppi(1), 250);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [fetchGruppi]);

  // ── Apri drawer dettaglio ──────────────────────────────────────────────────
  async function openDrawer(padreId: number) {
    setDrawerPadreId(padreId);
    setDrawerGruppo(null);
    setDrawerLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      // @api GET /api/articoli-intercambiabili/:padreId?societaId= -> {"success":"true","data":{"articoloPadre":{"id":"number","codice":"string"},"figli":[{"id":"number","codice":"string"}]}}
      const res = await apiFetch<{ success: boolean; data: GruppoDettaglio }>(
        `/api/articoli-intercambiabili/${padreId}?${params}`
      );
      setDrawerGruppo(res.data);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento dettaglio', 'error');
      setDrawerPadreId(null);
    } finally {
      setDrawerLoading(false);
    }
  }

  function closeDrawer() {
    setDrawerPadreId(null);
    setDrawerGruppo(null);
  }

  // ── Apri modal creazione ───────────────────────────────────────────────────
  function openCreateModal() {
    setEditPadreId(null);
    setEditPadreLabel('');
    setModalPadre(null);
    setModalFigli([]);
    setShowModal(true);
  }

  // ── Apri modal modifica ────────────────────────────────────────────────────
  function openEditModal(g: GruppoDettaglio) {
    setEditPadreId(g.articoloPadre.id);
    setEditPadreLabel(`${g.articoloPadre.codice} — ${g.articoloPadre.descrizione}`);
    const padreUm = g.articoloPadre.umVenditaId != null && g.articoloPadre.umVenditaSimbolo
      ? { id: g.articoloPadre.umVenditaId, simbolo: g.articoloPadre.umVenditaSimbolo }
      : null;
    setModalPadre({
      id: g.articoloPadre.id,
      codice: g.articoloPadre.codice,
      descrizioneBase: g.articoloPadre.descrizione,
      categoriaMerceologicaId: g.articoloPadre.categoriaMerceologicaId,
      categoriaMerceologica: g.articoloPadre.categoriaMerceologicaId != null && g.articoloPadre.nomeCategoria
        ? { id: g.articoloPadre.categoriaMerceologicaId, nome: g.articoloPadre.nomeCategoria }
        : null,
      umVenditaId: g.articoloPadre.umVenditaId,
      umVendita: padreUm,
    });
    setModalFigli(
      g.figli.map(f => ({
        id: f.articoloFiglioId,
        codice: f.codice,
        descrizioneBase: f.descrizione,
        umVenditaId: f.umVenditaId,
        umVendita: f.umVenditaId != null && f.um
          ? { id: f.umVenditaId, simbolo: f.um }
          : null,
      }))
    );
    setShowModal(true);
  }

  // ── Aggiungi/rimuovi figlio ────────────────────────────────────────────────
  function addFiglio(a: ArticoloOpzione) {
    if (modalFigli.some(f => f.id === a.id)) return;
    if (modalPadre && a.id === modalPadre.id) {
      showToast('Il figlio non può essere uguale al padre', 'error');
      return;
    }
    if (modalPadre && modalPadre.umVenditaId != null
        && a.umVenditaId != null && a.umVenditaId !== modalPadre.umVenditaId) {
      const umFiglio = a.umVendita?.simbolo ?? '—';
      const umPadre  = modalPadre.umVendita?.simbolo ?? '—';
      showToast(`U.M. di vendita "${umFiglio}" diversa dal padre "${umPadre}" — alternativo non aggiunto`, 'error');
      return;
    }
    setModalFigli(prev => [...prev, a]);
  }

  function removeFiglio(id: number) {
    setModalFigli(prev => prev.filter(f => f.id !== id));
  }

  // ── Salva gruppo ───────────────────────────────────────────────────────────
  async function handleSalva() {
    if (!modalPadre) return showToast('Seleziona un articolo padre', 'error');
    if (modalFigli.length === 0) return showToast('Aggiungi almeno un articolo figlio', 'error');

    setIsSaving(true);
    try {
      const figliIds = modalFigli.map(f => f.id);

      if (editPadreId !== null) {
        // Modifica (replace figli)
        const params = new URLSearchParams();
        if (filterSocietaId) params.set('societaId', filterSocietaId);
        // @api PUT /api/articoli-intercambiabili/:padreId {"figli":[1,2,3],"societaId":"optional"} -> {"success":"true","data":{"articoloPadreId":"number","nFigli":"number"}}
        await apiFetch(`/api/articoli-intercambiabili/${editPadreId}?${params}`, {
          method: 'PUT',
          body: JSON.stringify({ figli: figliIds, societaId: filterSocietaId ? parseInt(filterSocietaId, 10) : undefined }),
        });
        showToast('Gruppo aggiornato');
        setHighlightId(editPadreId);
        setTimeout(() => setHighlightId(null), 2000);
        // Aggiorna drawer se è aperto sullo stesso gruppo
        if (drawerPadreId === editPadreId) await openDrawer(editPadreId);
      } else {
        // Creazione
        if (!filterSocietaId) return showToast('Seleziona una società prima di creare un gruppo', 'error');
        // @api POST /api/articoli-intercambiabili {"societaId":"number","articoloPadreId":"number","figli":[1,2,3]} -> {"success":"true","data":{"articoloPadreId":"number","nFigliCreati":"number"}}
        await apiFetch('/api/articoli-intercambiabili', {
          method: 'POST',
          body: JSON.stringify({
            societaId: parseInt(filterSocietaId, 10),
            articoloPadreId: modalPadre.id,
            figli: figliIds,
          }),
        });
        showToast('Gruppo creato');
        setHighlightId(modalPadre.id);
        setTimeout(() => setHighlightId(null), 2000);
      }

      setShowModal(false);
      await fetchGruppi(editPadreId !== null ? page : 1);
    } catch (err: any) {
      showToast(err.message || 'Errore salvataggio', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Elimina gruppo ─────────────────────────────────────────────────────────
  async function handleElimina(padreId: number, codice: string) {
    if (!window.confirm(`Eliminare tutti i mapping del gruppo con padre "${codice}"?\nGli articoli non verranno eliminati dall'anagrafica.`)) return;
    try {
      const params = new URLSearchParams();
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      // @api DELETE /api/articoli-intercambiabili/:padreId?societaId= -> {"success":"true","data":{"deleted":"number"}}
      await apiFetch(`/api/articoli-intercambiabili/${padreId}?${params}`, { method: 'DELETE' });
      showToast('Gruppo eliminato');
      if (drawerPadreId === padreId) closeDrawer();
      await fetchGruppi(page);
    } catch (err: any) {
      showToast(err.message || 'Errore eliminazione', 'error');
    }
  }

  // ── Paginazione ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      {/* Toast */}
      {toast && (
        <div className={`amministrazione-toast ${toast.type === 'success' ? 'amministrazione-toast-success' : 'amministrazione-toast-error'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h2>Articoli Intercambiabili</h2>
          <p>Gruppi di articoli alternativi fra loro, associati per categoria merceologica</p>
        </div>
        <div className="intc-header-actions">
          <button className="bop-btn-secondary" onClick={handleScaricaTemplate}>
            ⬇ Scarica template
          </button>
          {canEdit && (
            <>
              <button
                className="bop-btn-secondary"
                onClick={openFilePicker}
                disabled={isImporting}
                title={!filterSocietaId ? 'Seleziona prima una società' : 'Importa da Excel'}
              >
                {isImporting ? 'Importazione…' : '⬆ Importa Excel'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="intc-file-input-hidden"
                onChange={handleFileSelected}
              />
              <button className="bop-btn-primary" onClick={openCreateModal}>
                + Nuovo Gruppo
              </button>
            </>
          )}
        </div>
      </div>

      {/* Regole gruppi intercambiabili */}
      <details className="intc-import-help intc-rules-help" open>
        <summary className="intc-import-help-summary">
          <span className="intc-import-help-icon intc-import-help-icon--warn" aria-hidden="true">!</span>
          <span className="intc-import-help-title">Regole per i gruppi intercambiabili</span>
          <span className="intc-import-help-toggle">apri / chiudi</span>
        </summary>
        <div className="intc-import-help-body">
          <ul className="intc-import-help-list intc-rules-list">
            <li>
              L'<strong>articolo PADRE</strong> deve avere tipologia <strong>INTERCAMBIABILE</strong> e una categoria merceologica valorizzata.
            </li>
            <li>
              Tutti gli <strong>articoli alternativi</strong> devono avere la <strong>stessa U.M. di vendita del padre</strong>.
              In caso contrario il gruppo non viene salvato (e in importazione la riga viene scartata con errore).
            </li>
            <li>
              Tutti gli articoli alternativi devono avere anche la <strong>stessa categoria merceologica</strong> del padre.
              Anche qui, in importazione la riga viene scartata con errore.
            </li>
            <li>
              Un articolo non può essere alternativo di sé stesso. I duplicati vengono rimossi automaticamente.
            </li>
          </ul>
        </div>
      </details>

      {/* Istruzioni importazione */}
      <details className="intc-import-help">
        <summary className="intc-import-help-summary">
          <span className="intc-import-help-icon" aria-hidden="true">ℹ</span>
          <span className="intc-import-help-title">Come funziona l'importazione da Excel</span>
          <span className="intc-import-help-toggle">apri / chiudi</span>
        </summary>
        <div className="intc-import-help-body">
          <p className="intc-import-help-lead">
            Usa <strong>Scarica template</strong> per ottenere il foglio precompilato, poi <strong>Importa Excel</strong> per caricarlo. L'import è per società: seleziona prima la società dalla topbar.
          </p>
          <div className="intc-import-help-cols">
            <div>
              <h4 className="intc-import-help-h4">Colonne</h4>
              <ul className="intc-import-help-list">
                <li><code>codice</code> — articolo PADRE (intercambiabile). Obbligatorio.</li>
                <li><code>descrizione_base</code> — usata solo se il padre non esiste e va creato.</li>
                <li><code>um_vendita</code> — usata solo se il padre non esiste (es. <code>mq</code>, <code>pz</code>).</li>
                <li><code>categoria_merceologica</code> — usata solo se il padre non esiste. Deve essere una categoria <strong>già presente</strong> in anagrafica (vedi <em>Guida valori ammessi</em> qui sotto).</li>
                <li><code>alternativo1 … alternativo6</code> — codici di articoli già esistenti.</li>
              </ul>
            </div>
            <div>
              <h4 className="intc-import-help-h4">Comportamento</h4>
              <ul className="intc-import-help-list">
                <li>Se il <strong>codice</strong> non esiste viene creato un articolo di tipologia <strong>INTERCAMBIABILE</strong> con la <strong>categoria merceologica indicata</strong>.</li>
                <li>Se esiste già con tipologia diversa la riga viene scartata.</li>
                <li>Se la categoria indicata non esiste in anagrafica la riga viene scartata: le categorie <strong>non</strong> vengono create dall'import.</li>
                <li>Se il padre esiste già, le colonne <code>descrizione_base</code>, <code>um_vendita</code> e <code>categoria_merceologica</code> vengono <strong>ignorate</strong>.</li>
                <li>Gli alternativi <strong>devono già esistere</strong> in anagrafica per la società e avere <strong>stessa U.M. e stessa categoria</strong> del padre. Anche un solo codice mancante o incoerente scarta l'intera riga.</li>
                <li>I mapping pre-esistenti del padre vengono <strong>sostituiti</strong> con quelli nuovi (replace completo).</li>
              </ul>
            </div>
          </div>
        </div>
      </details>

      {/* Guida valori ammessi — la fetch è dentro GuidaValoriAmmessi */}
      <GuidaValoriAmmessi
        societaId={filterSocietaId}
        intro={
          <>
            Valori tabellati usati dall'import Excel. Clicca su un valore per copiarlo
            negli appunti e incollalo <strong>esattamente come mostrato</strong>.
          </>
        }
        campi={v => [
          {
            nome: 'Categ. merceologica',
            colonnaExcel: 'categoria_merceologica',
            tipo: 'TABELLATO',
            valori: v.categorieMerceologiche.map(c => c.nome),
            nota: 'Obbligatoria quando l\'articolo padre va creato. Se il valore non è in elenco la riga viene scartata: l\'import non crea categorie nuove.',
          },
          {
            nome: 'U.M. di vendita',
            colonnaExcel: 'um_vendita',
            tipo: 'TABELLATO',
            valori: v.unitaMisura.map(u => u.simbolo),
            descrizioni: Object.fromEntries(v.unitaMisura.map(u => [u.simbolo, u.nome])),
            nota: 'Obbligatoria quando l\'articolo padre va creato. A differenza della categoria, un simbolo non presente viene creato automaticamente.',
          },
        ]}
      />

      {/* Filtri */}
      <div className="bop-card intc-filter-card">
        <div className="intc-filter-row">
          {!isUnica && (
            <select
              value={filterSocietaId}
              onChange={e => { setFilterSocietaId(e.target.value); setPage(1); }}
              className="bop-select intc-filter-select"
            >
              <option value="">Tutte le società</option>
              {listaSocieta.map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            className="bop-input intc-filter-search"
            placeholder="Cerca per codice o descrizione padre..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {categorie.length > 0 && (
            <select
              value={filterCategoriaId}
              onChange={e => { setFilterCategoriaId(e.target.value); setPage(1); }}
              className="bop-select intc-filter-select"
            >
              <option value="">Tutte le categorie</option>
              {categorie.map(c => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Layout principale: tabella + drawer */}
      <div className={`intc-layout ${drawerPadreId !== null ? 'intc-layout--with-drawer' : ''}`}>
        {/* Tabella gruppi */}
        <div className="bop-card intc-table-card">
          {isLoading ? (
            <div className="intc-loading">Caricamento gruppi...</div>
          ) : gruppi.length === 0 ? (
            <div className="intc-empty">
              <svg className="intc-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
              </svg>
              <p className="intc-empty-text">Nessun gruppo intercambiabile trovato</p>
              {canEdit && (
                <button className="bop-btn-primary" onClick={openCreateModal}>
                  Crea il primo gruppo
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="intc-table-scroll">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Articolo Padre</th>
                      <th>Categoria Merceologica</th>
                      <th className="bop-text-center">Alternativi</th>
                      {!isUnica && <th>Società</th>}
                      <th className="bop-text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gruppi.map(g => {
                      const isActive = drawerPadreId === g.articoloPadreId;
                      const isHighlight = highlightId === g.articoloPadreId;
                      return (
                        <tr
                          key={g.articoloPadreId}
                          className={[
                            isActive ? 'intc-row-active' : '',
                            isHighlight ? 'intc-row-highlight' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <td>
                            <div className="intc-articolo-cell">
                              <Link
                                to={`/anagrafiche/articoli/${g.articoloPadreId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="intc-codice intc-codice--link"
                                title="Apri la scheda articolo in una nuova scheda"
                                onClick={e => e.stopPropagation()}
                              >
                                {g.codicePadre}
                              </Link>
                              <span className="intc-descrizione">{g.descrizionePadre}</span>
                            </div>
                          </td>
                          <td>
                            {g.nomeCategoria ? (
                              <span className="intc-categoria-badge">{g.nomeCategoria}</span>
                            ) : (
                              <span className="bop-text-muted">—</span>
                            )}
                          </td>
                          <td className="bop-text-center">
                            <span className="intc-count-badge">{g.nFigli}</span>
                          </td>
                          {!isUnica && (
                            <td className="bop-text-muted">{g.nomeSocieta ?? '—'}</td>
                          )}
                          <td>
                            <div className="intc-actions">
                              <button
                                className="bop-btn-secondary intc-btn-small"
                                onClick={() => isActive ? closeDrawer() : openDrawer(g.articoloPadreId)}
                              >
                                {isActive ? 'Chiudi' : 'Dettaglio'}
                              </button>
                              {canDelete && (
                                <button
                                  className="bop-btn-danger intc-btn-small"
                                  onClick={() => handleElimina(g.articoloPadreId, g.codicePadre)}
                                >
                                  Elimina
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Paginazione */}
              {totalPages > 1 && (
                <div className="intc-pagination">
                  <span className="intc-pagination-info">
                    {total} gruppi — pagina {page} di {totalPages}
                  </span>
                  <div className="intc-pagination-controls">
                    <button
                      className="bop-btn-secondary intc-btn-small"
                      disabled={page <= 1}
                      onClick={() => fetchGruppi(page - 1)}
                    >
                      ← Precedente
                    </button>
                    <button
                      className="bop-btn-secondary intc-btn-small"
                      disabled={page >= totalPages}
                      onClick={() => fetchGruppi(page + 1)}
                    >
                      Successiva →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Drawer dettaglio */}
        {drawerPadreId !== null && (
          <div className="intc-drawer">
            <div className="intc-drawer-header">
              <div className="intc-drawer-title">
                {drawerGruppo ? (
                  <>
                    <Link
                      to={`/anagrafiche/articoli/${drawerGruppo.articoloPadre.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="intc-drawer-codice intc-drawer-codice--link"
                      title="Apri la scheda articolo in una nuova scheda"
                    >
                      {drawerGruppo.articoloPadre.codice}
                    </Link>
                    <span className="intc-drawer-desc">{drawerGruppo.articoloPadre.descrizione}</span>
                  </>
                ) : (
                  <span className="intc-drawer-loading-title">Caricamento...</span>
                )}
              </div>
              <button className="bop-btn-icon intc-drawer-close" onClick={closeDrawer} title="Chiudi">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {drawerLoading ? (
              <div className="intc-drawer-loading">Caricamento dettaglio...</div>
            ) : drawerGruppo ? (
              <>
                {/* Metadati gruppo */}
                {drawerGruppo.articoloPadre.nomeCategoria && (
                  <div className="intc-drawer-meta">
                    <span className="intc-drawer-meta-label">Categoria:</span>
                    <span className="intc-categoria-badge">{drawerGruppo.articoloPadre.nomeCategoria}</span>
                  </div>
                )}

                {/* Azioni drawer */}
                {canEdit && (
                  <div className="intc-drawer-actions">
                    <button
                      className="bop-btn-primary intc-btn-small"
                      onClick={() => openEditModal(drawerGruppo)}
                    >
                      Modifica alternativi
                    </button>
                  </div>
                )}

                {/* Lista figli */}
                <div className="intc-drawer-section-title">
                  Articoli alternativi ({drawerGruppo.figli.length})
                </div>

                {drawerGruppo.figli.length === 0 ? (
                  <div className="intc-drawer-empty">Nessun articolo figlio</div>
                ) : (
                  <ul className="intc-figli-list">
                    {drawerGruppo.figli.map(f => (
                      <li key={f.id} className="intc-figlio-item">
                        <div className="intc-figlio-header">
                          <Link
                            to={`/anagrafiche/articoli/${f.articoloFiglioId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="intc-codice intc-codice--link"
                            title="Apri la scheda articolo in una nuova scheda"
                          >
                            {f.codice}
                          </Link>
                          {f.um && <span className="intc-um-badge">{f.um}</span>}
                        </div>
                        <span className="intc-descrizione">{f.descrizione}</span>
                        <div className="intc-figlio-meta">
                          {f.fornitore && (
                            <span className="intc-figlio-fornitore" title="Fornitore">
                              {f.fornitore}
                            </span>
                          )}
                          {f.prezzo !== null && (
                            <span className="intc-figlio-prezzo">
                              {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(f.prezzo)}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Modal Crea / Modifica */}
      {showModal && (
        <div className="amministrazione-modal-overlay">
          <div className="intc-modal-content">
            <h3 className="amministrazione-modal-title">
              {editPadreId !== null ? 'Modifica alternativi' : 'Nuovo gruppo intercambiabili'}
            </h3>

            <div className="intc-modal-body">
              {/* Articolo padre */}
              <div className="bop-form-group">
                <label className="bop-label">Articolo padre *</label>
                {editPadreId !== null ? (
                  <div className="intc-padre-readonly">{editPadreLabel}</div>
                ) : (
                  <>
                    <AutocompleteArticolo
                      placeholder="Cerca articolo padre (tipologia INTERCAMBIABILE)..."
                      societaId={filterSocietaId}
                      tipologia="INTERCAMBIABILE"
                      escludiIds={modalFigli.map(f => f.id)}
                      onSelect={a => setModalPadre(a)}
                    />
                    {modalPadre && (
                      <>
                        <div className="intc-padre-selected">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--bop-color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span className="intc-codice">{modalPadre.codice}</span>
                          <span className="intc-descrizione">{modalPadre.descrizioneBase}</span>
                          {modalPadre.umVendita && (
                            <span className="intc-um-badge">{modalPadre.umVendita.simbolo}</span>
                          )}
                          <button className="intc-padre-clear" onClick={() => setModalPadre(null)}>✕</button>
                        </div>
                        <p className="intc-padre-categoria">
                          Categoria merceologica: <strong>{modalPadre.categoriaMerceologica?.nome ?? '—'}</strong>
                          {' · '}
                          U.M. vendita: <strong>{modalPadre.umVendita?.simbolo ?? '—'}</strong>
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Articoli figli */}
              <div className="bop-form-group">
                <label className="bop-label">
                  Articoli alternativi *
                  <span className="intc-label-hint">
                    Verde = stessa categoria e U.M. del padre · Rosso = differenza · L'U.M. di vendita deve coincidere col padre.
                  </span>
                </label>
                <AutocompleteArticolo
                  placeholder="Cerca e aggiungi articolo alternativo..."
                  societaId={filterSocietaId}
                  escludiIds={[
                    ...(modalPadre ? [modalPadre.id] : []),
                    ...modalFigli.map(f => f.id),
                  ]}
                  onSelect={addFiglio}
                  padreCategoriaMerceologicaId={modalPadre?.categoriaMerceologica?.id ?? null}
                  padreUmVenditaId={modalPadre?.umVenditaId ?? modalPadre?.umVendita?.id ?? null}
                />
                {modalFigli.length > 0 && (
                  <ul className="intc-modal-figli-list">
                    {modalFigli.map(f => {
                      const figlioUmId = f.umVenditaId ?? f.umVendita?.id ?? null;
                      const padreUmId  = modalPadre?.umVenditaId ?? modalPadre?.umVendita?.id ?? null;
                      const umMismatch = padreUmId != null && figlioUmId != null && padreUmId !== figlioUmId;
                      return (
                        <li key={f.id} className={`intc-modal-figlio-item ${umMismatch ? 'intc-modal-figlio-item--um-mismatch' : ''}`}>
                          <span className="intc-codice">{f.codice}</span>
                          <span className="intc-descrizione">{f.descrizioneBase}</span>
                          {f.umVendita && (
                            <span className={`intc-um-badge ${umMismatch ? 'intc-um-badge--err' : ''}`}>
                              {f.umVendita.simbolo}
                            </span>
                          )}
                          <button
                            className="intc-remove-btn"
                            onClick={() => removeFiglio(f.id)}
                            title="Rimuovi"
                          >
                            ✕
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {modalFigli.length === 0 && (
                  <p className="intc-modal-figli-empty">Nessun articolo alternativo aggiunto</p>
                )}
              </div>
            </div>

            <div className="amministrazione-modal-actions">
              <button className="bop-btn-secondary" onClick={() => setShowModal(false)}>
                Annulla
              </button>
              <button
                className="bop-btn-primary"
                onClick={handleSalva}
                disabled={isSaving}
              >
                {isSaving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal esito import */}
      {importResult && (
        <div className="amministrazione-modal-overlay">
          <div className="intc-modal-content intc-import-modal">
            <h3 className="amministrazione-modal-title">
              Esito import — <span className={`intc-import-stato intc-import-stato--${importResult.stato.toLowerCase()}`}>{importResult.stato.replace(/_/g, ' ')}</span>
            </h3>

            {importResult.fatalError ? (
              <div className="intc-import-fatal">
                <strong>Errore:</strong> {importResult.fatalError}
              </div>
            ) : (
              <div className="intc-import-summary">
                <div className="intc-import-stat">
                  <span className="intc-import-stat-num">{importResult.totaleRighe}</span>
                  <span className="intc-import-stat-label">Righe totali</span>
                </div>
                <div className="intc-import-stat">
                  <span className="intc-import-stat-num">{importResult.padreCreati}</span>
                  <span className="intc-import-stat-label">Padri creati</span>
                </div>
                <div className="intc-import-stat">
                  <span className="intc-import-stat-num">{importResult.padreRiusati}</span>
                  <span className="intc-import-stat-label">Padri riusati</span>
                </div>
                <div className="intc-import-stat">
                  <span className="intc-import-stat-num">{importResult.gruppiAggiornati}</span>
                  <span className="intc-import-stat-label">Gruppi aggiornati</span>
                </div>
                <div className={`intc-import-stat ${importResult.righeErrore > 0 ? 'intc-import-stat--err' : ''}`}>
                  <span className="intc-import-stat-num">{importResult.righeErrore}</span>
                  <span className="intc-import-stat-label">Righe in errore</span>
                </div>
              </div>
            )}

            <pre className="intc-import-log">{importResult.logTesto}</pre>

            <div className="amministrazione-modal-actions">
              <button className="bop-btn-primary" onClick={() => setImportResult(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
